<script setup lang="ts">
  import { cn } from '@/lib/utils'
  import { X } from 'lucide-vue-next'
  import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogOverlay,
    DialogPortal,
    useForwardPropsEmits
  } from 'radix-vue'
  import { type HTMLAttributes, computed, useAttrs } from 'vue'

  defineOptions({
    inheritAttrs: false
  })

  const props = defineProps<
    DialogContentProps & {
      class?: HTMLAttributes['class']
      mobileBottomSheet?: boolean
      overlayClass?: HTMLAttributes['class']
    }
  >()
  const emits = defineEmits<DialogContentEmits>()
  const attrs = useAttrs()

  const delegatedProps = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { class: _, mobileBottomSheet: __, overlayClass: ___, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      id="dialog-overlay"
      :class="
        cn(
          `fixed inset-0 z-[56] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
          props.overlayClass
        )
      "
    />
    <DialogContent
      v-bind="{ ...forwarded, ...attrs }"
      id="dialog-content"
      :class="
        cn(
          `fixed left-1/2 top-1/2 z-[56] grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border
          bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in
          data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
          data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
          data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]
          data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg`,
          props.mobileBottomSheet &&
            `max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:top-auto max-sm:max-w-full max-sm:translate-x-0
            max-sm:translate-y-0 max-sm:transform-none max-sm:rounded-b-none max-sm:rounded-l-none
            max-sm:rounded-r-none max-sm:rounded-t-2xl max-sm:border-b-0 max-sm:shadow-lg`,
          props.class
        )
      "
    >
      <slot />

      <DialogClose
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity
          hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        data-cy="dialogCloseBtn"
      >
        <X class="h-4 w-4" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
